import React, { useState, useContext } from "react"
import { ThemeProvider, createTheme, styled } from "@mui/material/styles"
import { Box } from "@mui/material"
import { useNavigate } from "react-router-dom"
import Header from "../../layouts/Header"
import BackIcon from "../../components/BackIcon"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import ModalImportCSV from "../../components/CustomCSVImportButton"
import { GlobalContext } from "../../contexts/GlobalContext"

// Importaciones para las acciones dinámicas, mutaciones y exportaciones
import { useQueryClient } from "@tanstack/react-query"
import { useMutation, api } from "../../api"
import getIconComponent from "../utils/getIconComponent"
import CustomBackdrop from "../../components/CustomBackdrop"
import {
  handleExportDataPdfLGScreen,
  handleExportDataPdfSMScreen,
  handleAllExportDataCSV,
} from "../utils/reactTableActions/exportToolbarActions"

// Estilos base para el layout principal
const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

// Tema estándar de SIACDEV1.0
const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const SubTiposPPE = () => {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Estado para controlar la apertura del modal de importación
  const [openImportModal, setOpenImportModal] = useState(false)

  // Mutación para eliminación con recarga de grilla
  const { mutateAsync: SaveEliminacionSubTipoActivo, isPending: isDeletingSubTipoActivo } = useMutation({
    queryKey: ["isDeletingSubTipoActivo"],
    fn: async (data) => {
      const response = await api.post("/SubTiposPPE/eliminarSubTiposPPE", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["SubTiposPPE"] })
    },
  })

  // Columnas a mostrar en la tabla principal
  const columnsTable = [
    { accessorKey: "gencodigo", header: "Cód. Sub Tipo", size: 120 },
    { accessorKey: "gendescri", header: "Descripción", size: 250 },
    { accessorKey: "tipcodigo", header: "Cód. Padre", size: 100 },
    { accessorKey: "tipdescri", header: "Tipo Padre", size: 200 },
    {
      accessorKey: "genstatus",
      header: "Estado",
      size: 100,
      Cell: ({ cell }) => (cell.getValue() === "A" ? "ACTIVO" : "INACTIVO"),
    },
    {
      accessorKey: "tipplacas",
      header: "Usa Placas",
      size: 100,
      Cell: ({ cell }) => (cell.getValue() === 1 ? "SÍ" : "NO"),
    },
    {
      accessorKey: "tipcodcatastral",
      header: "Usa Catastral",
      size: 120,
      Cell: ({ cell }) => (cell.getValue() === 1 ? "SÍ" : "NO"),
    },
    {
      accessorKey: "tipsuperficie",
      header: "Usa Superficie",
      size: 120,
      Cell: ({ cell }) => (cell.getValue() === 1 ? "SÍ" : "NO"),
    },
    {
      accessorKey: "tipserie",
      header: "Usa Serie",
      size: 100,
      Cell: ({ cell }) => (cell.getValue() === 1 ? "SÍ" : "NO"),
    },
    {
      accessorKey: "tipdeprecia",
      header: "Deprecia",
      size: 100,
      Cell: ({ cell }) => (cell.getValue() === 1 ? "SÍ" : "NO"),
    },
  ]

  // Configuración de validación para el CSV (columnas clave según la tabla afbgen)
  const fieldConfigs = {
    gencodigo: { required: true, key: true },
    tipcodigo: { required: true, key: true },
    gendescri: { required: true },
    tipplacas: { required: false },
    tipcodcatastral: { required: false },
    tipsuperficie: { required: false },
    tipserie: { required: false },
    tipdeprecia: { required: false },
    genstatus: { required: false },
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Gestión de Sub Tipos de Activos (PPE)</b>
        </div>

        <CustomBackdrop isLoading={isDeletingSubTipoActivo} />

        <StyledRoot>
          {/* Modal de Importación CSV integrado */}
          <ModalImportCSV
            open={openImportModal}
            onClose={() => setOpenImportModal(false)}
            templateFileName="Plantilla_SubTiposActivosPPE.csv"
            fieldConfigs={fieldConfigs}
            maxFileSize={10 * 1024 * 1024}
            validateEndpoint="/SubTiposPPE/validarSubTiposPPEIMP"
            insertEndpoint="/SubTiposPPE/insertarSubTiposPPEIMP"
            onImportComplete={() => qc.invalidateQueries({ queryKey: ["SubTiposPPE"] })}
          />

          <CustomConditionalActionsTableServer
            endpoint="/SubTiposPPE/getAllSubTiposPPE"
            errorMsgFilterSearch="Error en cargar datos"
            queryKeyModal="SubTiposPPE"
            perPage={10}
            rowActionsWidthTable={120}
            columnsTable={columnsTable}
            // ACCIONES DE FILA (Editar y Eliminar)
            rowActions={(row) => {
              const editarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "EDITAR",
              )
              const eliminarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "ELIMINAR",
              )

              const actions = [
                {
                  label: editarAction?.acccaption || "Editar",
                  key: editarAction?.acccaption || "EDITAR",
                  icon: getIconComponent(editarAction?.accnameicono, editarAction?.acctipoico),
                  onClick: (row) => {
                    navigate("editar", { state: row.original })
                  },
                },
                {
                  label: eliminarAction?.acccaption || "Eliminar",
                  key: eliminarAction?.acccaption || "ELIMINAR",
                  icon: getIconComponent(eliminarAction?.accnameicono, eliminarAction?.acctipoico),
                  onClick: async (row) => {
                    if (window.confirm(`¿Está seguro de eliminar el Sub Tipo ${row.original.gencodigo}?`)) {
                      try {
                        await SaveEliminacionSubTipoActivo(row.original)
                      } catch (err) {
                        console.error("Error eliminando el sub tipo de activo:", err)
                      }
                    }
                  },
                },
              ]
              return actions
            }}
            // BARRA DE HERRAMIENTAS SUPERIOR (Crear, Exportar, Importar)
            topToolbarCustomActions={({ table, device }) => {
              const crearAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action.acccaption === "CREAR")
              const exportarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action.acccaption === "EXPORTAR",
              )

              const toolbarActions = [
                {
                  label: crearAction?.acccaption || "Crear",
                  key: crearAction?.acccaption || "CREAR",
                  icon: getIconComponent(crearAction?.accnameicono, crearAction?.acctipoico),
                  onClick: () => {
                    navigate("crear")
                  },
                },
                {
                  type: "dropdown",
                  label: exportarAction?.acccaption || "Exportar",
                  key: "exportarDropdown",
                  icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                  actions: [
                    {
                      label: "Exportar PDF",
                      key: "exportarPDF",
                      icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                      onClick: ({ columns, data }) => {
                        if (device === "sm") {
                          return handleExportDataPdfSMScreen(
                            columns,
                            data,
                            "Reporte de Sub Tipos de Activos",
                            `Reporte_SubTipos_Activos_${new Date().toLocaleString()}`,
                          )
                        }
                        handleExportDataPdfLGScreen(
                          columns,
                          table.getCoreRowModel().rows,
                          "Reporte de Sub Tipos de Activos",
                          `Reporte_SubTipos_Activos_${new Date().toLocaleString()}`,
                        )
                      },
                    },
                    {
                      label: "Exportar CSV",
                      key: "exportarCSV",
                      icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                      onClick: ({ data }) => {
                        handleAllExportDataCSV(data, `Reporte_SubTipos_Activos_${new Date().toLocaleString()}`)
                      },
                    },
                  ],
                },
                {
                  label: "Importar",
                  key: "importarDropdown",
                  icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico), // Mantiene la lógica de tu archivo
                  onClick: () => {
                    setOpenImportModal(true)
                  },
                },
              ]
              return toolbarActions
            }}
          />
        </StyledRoot>
      </div>
    </ThemeProvider>
  )
}

export default SubTiposPPE
